
import { makeOpenAIRequest } from '../deep-content-analysis/apiClient';
import { FAQResponse } from '@/components/faq-serp/types';
import { logger } from '@/utils/logger';
import { getOpenAIKey } from '@/utils/apiKeys';
import { MODEL_SETTINGS, TEMPERATURE_SETTINGS, MAX_TOKENS_SETTINGS, RESPONSE_FORMAT_SETTINGS } from '@/hooks/api/modelSettings';
import { cleanContent, logRequestDetails } from './utils/requestUtils';
import { parseJsonResponse } from './utils/responseUtils';
import { validateApiKey } from './utils/apiValidation';
import { getSystemPrompt, getUserPrompt } from './prompts';

/**
 * Analyzes content to generate FAQs using OpenAI
 */
export async function generateFAQs(
  content: string,
  domain: string
): Promise<FAQResponse> {
  // Get the API key specifically for FAQ generation from the centralized store
  const apiKey = getOpenAIKey('faqSerp') || process.env.OPENAI_API_KEY || '';
  
  // Validate the API key
  validateApiKey(apiKey);
  
  // Clean the content to remove any potential image or metadata references
  const cleanedContent = cleanContent(content);
  
  // Get model settings from central configuration
  const model = MODEL_SETTINGS.faqSerp;
  const temperature = TEMPERATURE_SETTINGS.faqSerp;
  const maxTokens = MAX_TOKENS_SETTINGS.faqSerp;
  const responseFormat = RESPONSE_FORMAT_SETTINGS.faqSerp;
  
  // Log the request details for debugging
  logger.info('Generating FAQs', {
    context: {
      component: 'faqExpertService',
      contentLength: cleanedContent.length,
      domain
    }
  });
  
  try {
    // Generate the prompts
    const systemPrompt = getSystemPrompt();
    const userPrompt = getUserPrompt(domain, cleanedContent);
    
    // Log request details
    logRequestDetails(apiKey, model, temperature, maxTokens, responseFormat);
    
    // Make the API request with the settings from modelSettings
    const responseText = await makeOpenAIRequest(
      apiKey,
      model,
      systemPrompt,
      userPrompt,
      temperature,
      maxTokens,
      false, // Don't use schema for FAQ generation
      responseFormat as 'json_object' | 'text' // Pass the response format
    );
    
    // Log the raw response for debugging
    console.log('FAQ Expert raw response type:', typeof responseText);
    if (typeof responseText === 'string') {
      console.log('FAQ Expert raw response first 100 chars:', responseText.substring(0, 100));
    } else {
      console.log('FAQ Expert received object response:', JSON.stringify(responseText).substring(0, 100));
    }
    
    // Parse and validate the JSON response
    const parsedResponse = parseJsonResponse(responseText);
    
    // Log success
    logger.info('FAQs generated successfully', {
      context: {
        component: 'faqExpertService',
        faqCount: Object.values(parsedResponse.faq).flat().length
      }
    });
    
    return parsedResponse;
  } catch (error) {
    // Log the error
    logger.error('Error generating FAQs', error as Error);
    
    throw error;
  }
}
